
import React, { useEffect, useMemo, useState } from 'react';
import { propagateQuerytoExternalLinks, urlIsExternal, applyGanalyticsTracking } from 'src/functions/propagateQueryParams';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';

import { palette } from 'src/styles/colors';
import getButtonStyleByColorScheme from 'src/styles/functions/getButtonStyleByColorScheme';
import { StyledLinkIconSpan, StyledLinkSimple, StyledLinkSimpleProps } from './Link.simple.styles';

export interface LinkSimpleProps extends StyledLinkSimpleProps {
  text: string;
  underlined?: boolean;
  color: 'main' | 'text';
  colorScheme?: 'Standard' | 'Pro';
  url?: string;
  smooth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  justify?: 'stretch' | 'center' | 'left' | 'right';
}

const LinkSimple = ({ text,
  url,
  justify,
  color,
  underlined,
  icon,
  smooth,
  size,
  sizeLg,
  sizeSubLg,
  sizeMd,
  iconPosition,
  colorScheme = 'Standard'
}: LinkSimpleProps): JSX.Element => {

  const [ queryPropagatedUrl, updateHref ] = useState(url);

  useEffect(() => {
    windowIsLoaded().then(() => {
      updateHref(propagateQuerytoExternalLinks(url));
    })
  }, [ url ])

  const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    document.querySelector(url).scrollIntoView({
      behavior: 'smooth'
    });
  }

  // Smooth scroll only works when url is ID type
  const navigationProp = smooth && url.includes('#') ? { onClick } : { href: queryPropagatedUrl || '#', onClick: applyGanalyticsTracking(queryPropagatedUrl, url) };
  const targetProp = useMemo(() => {
    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [ url ])
  const linkColors = useMemo(() => getButtonStyleByColorScheme(colorScheme, 'secondary'), []);

  return (
    <StyledLinkSimple
      justify={justify}
      textColor={color === 'text' ? palette.text : linkColors.textColor}
      hasIcon={!!icon}
      iconPosition={iconPosition}
      underlined={underlined}
      size={size}
      sizeLg={sizeLg}
      sizeSubLg={sizeSubLg}
      sizeMd={sizeMd}
      {...navigationProp}
      {...targetProp}
    >
      {icon ? <StyledLinkIconSpan iconPosition={iconPosition} color={color}> {icon}</StyledLinkIconSpan> : null}

      <span>
        {text}
      </span>
    </StyledLinkSimple>
  );
};

export default LinkSimple;
